import React from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { SkeletonText } from '@shared/components/Skeleton';
import { pxToRem } from '@withjoy/joykit/theme';
import { FoilBadge } from '../../FoilBadge';
import { CardFormat } from '../constants';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import { useGetDiscountByVendor } from '../../DesignsGallery/components/GalleryItem/hooks/useGetDiscountByVendor';
import { CardTileDiscountedPriceLabel } from '../../DesignsGallery/components/CardTileDiscountedPriceLabel';
import { useTranslation } from '@shared/core';

interface TitleBlockProps {
  title: string;
  subtitle?: string;
  isFoil?: boolean;
  format?: CardFormat;
}

const Root: React.FC = ({ children }) => {
  return (
    <Flex
      flexDirection="column"
      rowGap={3}
      marginBottom={{ _: 5, sm2: 8 }}
    >
      {children}
    </Flex>
  );
};
export const TitleBlock = (props: TitleBlockProps) => {
  const { title, subtitle, format, isFoil = false } = props;
  const { tq } = useTranslation('stationery');

  // For our consideration. Foil is a different product in our eyes?
  // Or is it an attribute? Either way, PrintPriceCalculator should
  // understand the cost of foil as a part of the product configuration.
  // const { pricePerCardInMinorUnits: foilPrice } = useFoilConfig();
  // const displayPrice = useDisplayPrice({ taylor: isFoil ? foilPrice : 0 });
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const { getDiscountByVendor } = useGetDiscountByVendor();

  const { activeProduct, priceCalculator } = useStationeryProductPricingContext();
  const discount = getDiscountByVendor(activeProduct.vendor);

  const itemPricing = priceCalculator.getProductBasePrice(activeProduct.id, activeProduct.defaultQuantity, discount);
  const formattedUnitPrice = minorUnitsToPriceString(itemPricing.unitPriceInMinorUnits, { canShowFreeLabel: true });

  const discountInfo = itemPricing.discounts;
  const formattedPreDiscountPrice = discountInfo && minorUnitsToPriceString(discountInfo.preDiscountedUnitPriceInMinorUnits, { canShowFreeLabel: true });
  const canShowDiscount = itemPricing.discounts?.savingsInMinorUnits !== 0;

  return (
    <Root>
      {isFoil && <FoilBadge />}
      <TextV2
        fontWeight={{ sm2: 600 }}
        typographyVariant={{ _: 'hed4', sm2: 'display1' }}
      >
        {title}
      </TextV2>
      {formattedPreDiscountPrice && discount && canShowDiscount ? (
        <CardTileDiscountedPriceLabel
          preDiscountPrice={formattedPreDiscountPrice}
          sale={discount.fullPromoConfig}
          discountedPrice={formattedUnitPrice}
          showCode={true}
          alignment="flex-start"
        />
      ) : (
        <Flex
          height={pxToRem(24)}
          alignItems="center"
        >
          <TextV2 typographyVariant="label2">{format === CardFormat.digital ? tq`Free` : tq`From ${formattedUnitPrice}`}</TextV2>
        </Flex>
      )}
      {subtitle && (
        <TextV2
          color="mono12"
          fontWeight={{ _: 500, sm2: 500 }}
          typographyVariant={{ _: 'hed1', sm2: 'hed4' }}
        >
          {subtitle}
        </TextV2>
      )}
    </Root>
  );
};

TitleBlock.Skeleton = () => {
  return (
    <Root>
      <SkeletonText
        rows={1}
        width={{ _: 160, sm2: 200 }}
        skeletonHeight={{ _: 24, sm2: 30 }}
      />
      <SkeletonText
        rows={1}
        width={80}
        skeletonHeight={{ _: 20, sm2: 24 }}
      />
    </Root>
  );
};
